import React from 'react';
import { Icon } from './Icon';
import UploadIndicator, { uploadCaption } from './UploadIndicator';
import { getUploadLabels } from '../lib/fileValidation';
import type { UploadProgress } from '../lib/upload/retry';

/**
 * Single square photo card with an optional cover star, badge, hover
 * action overlay and drag-reorder hooks. Visual baseline for every
 * "photos grid" in the app — portadas, variant photos, design photos,
 * piece photos. Don't reach for raw <img> + overlay markup; reuse this.
 *
 * Action set is opt-in (each handler renders its own icon). Pending /
 * marked-deleted / over-limit / hidden are mutually-exclusive visual
 * states with reserved styles; the corresponding action set adjusts
 * automatically (e.g. marked-deleted shows only an undo button).
 */
export interface PhotoThumbProps {
    /** URL of the rendered image (prefer a thumbnail URL). */
    url: string;
    /** Optional full-res URL used by the download button (defaults to ``url``). */
    downloadUrl?: string;
    /** File name used for the ``download`` attribute. */
    downloadFileName?: string;
    alt?: string;

    /** Density. ``sm`` for dense product galleries (cols-6/8/10);
     *  ``md`` for the design photos grid (cols-3). */
    size?: 'sm' | 'md';

    /** Cover indicator (yellow star top-left). */
    isCover?: boolean;
    coverLabel?: string;

    /** Bottom-right pill, e.g. SKU or "P12". ``null`` hides it. */
    badge?: string | null;
    badgeTooltip?: string;

    /** Distinct visual states. Mutually exclusive. */
    state?: 'pending' | 'marked-deleted' | 'over-limit' | 'hidden';

    /** Download action — anchor target. */
    onDownload?: { url: string; fileName?: string };
    onToggleHidden?: () => void;
    isHidden?: boolean;
    onDelete?: () => void;
    onUndoDelete?: () => void;
    /** When true the delete button still renders but is non-interactive
     *  and shows ``disableDeleteReason`` as tooltip. Used by inherited
     *  design photos that can't be deleted from the design page. */
    disableDelete?: boolean;
    disableDeleteReason?: string;

    downloadAriaLabel?: string;
    hideAriaLabel?: string;
    showAriaLabel?: string;
    deleteAriaLabel?: string;
    undoAriaLabel?: string;
    overLimitLabel?: string;

    draggable?: boolean;
    isDragging?: boolean;
    onDragStart?: (e: React.DragEvent) => void;
    onDragOver?: (e: React.DragEvent) => void;
    onDragEnd?: () => void;

    /** Fires when the user clicks the thumbnail body (not the action
     *  buttons). Useful for opening a preview lightbox. When set, the
     *  cursor turns into a pointer. */
    onClick?: () => void;

    /** The photo is still uploading (``url`` is then its local preview): a
     *  progress ring in the corner and no other action until it lands. A
     *  failed one shows a retry button (`onRetryUpload`). */
    upload?: UploadProgress;
    onRetryUpload?: () => void;
    /** Cancels the upload in progress (or drops a failed one). */
    onCancelUpload?: () => void;

    className?: string;
}

const PhotoThumb: React.FC<PhotoThumbProps> = ({
    url,
    downloadUrl,
    downloadFileName,
    alt,
    size = 'sm',
    isCover = false,
    coverLabel,
    badge,
    badgeTooltip,
    state,
    onDownload,
    onToggleHidden,
    isHidden = false,
    onDelete,
    onUndoDelete,
    disableDelete = false,
    disableDeleteReason,
    downloadAriaLabel,
    hideAriaLabel,
    showAriaLabel,
    deleteAriaLabel,
    undoAriaLabel,
    overLimitLabel,
    draggable = false,
    isDragging = false,
    onDragStart,
    onDragOver,
    onDragEnd,
    onClick,
    upload,
    onRetryUpload,
    onCancelUpload,
    className = '',
}) => {
    const radiusCls = size === 'sm' ? 'rounded-md' : 'rounded-lg';
    const iconCls = size === 'sm' ? '!text-[14px]' : '!text-[18px]';
    const starIconCls = size === 'sm' ? '!text-[10px]' : '!text-[14px]';
    const badgeCls = size === 'sm'
        ? 'bottom-0.5 right-0.5 px-1 py-0.5 text-[8px]'
        : 'bottom-1 right-1 px-1.5 py-0.5 text-[10px]';
    const starCls = size === 'sm'
        ? 'top-0.5 left-0.5 p-0.5'
        : 'top-1 left-1 p-0.5';

    const isMarkedDelete = state === 'marked-deleted';
    const isOverLimit = state === 'over-limit';
    const isPending = state === 'pending';
    const isStateHidden = state === 'hidden' || isHidden;

    // ── Uploading: the local preview, a progress ring in the corner, nothing
    // else to click but cancel (and retry once it failed).
    if (upload) {
        const labels = getUploadLabels();
        const failed = upload.kind === 'failed';
        return (
            <div
                className={`relative ${radiusCls} overflow-hidden aspect-square border ${failed ? 'border-red-500/70' : 'border-border-subtle dark:border-border-subtle-dark'} bg-slate-50 dark:bg-surface-hover-dark ${className}`}
                title={uploadCaption(upload)}
            >
                <img
                    src={url}
                    alt={alt}
                    draggable={false}
                    className={`w-full h-full object-cover pointer-events-none ${failed ? 'opacity-40 grayscale' : 'opacity-70'}`}
                />
                {failed && upload.retryable !== false && onRetryUpload && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onRetryUpload(); }}
                            className="bg-white/90 dark:bg-black/80 p-1.5 rounded-full text-primary hover:text-primary-dark shadow-sm transition-colors"
                            title={labels.uploadRetry}
                            aria-label={labels.uploadRetry}
                        >
                            <Icon name="refresh" className={iconCls} />
                        </button>
                    </div>
                )}
                {/* Fixed square chips: an inline box takes the line height and
                    turns the circle into a pill. */}
                <span className={`absolute ${size === 'sm' ? 'bottom-0.5 right-0.5 h-[22px] w-[22px]' : 'bottom-1 right-1 h-[32px] w-[32px]'} flex items-center justify-center rounded-full bg-white/90 dark:bg-surface-dark/90 shadow-sm`}>
                    <UploadIndicator progress={upload} size={size === 'sm' ? 'sm' : 'md'} />
                </span>
                {onCancelUpload && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onCancelUpload(); }}
                        className={`absolute ${size === 'sm' ? 'top-0.5 right-0.5 h-5 w-5' : 'top-1 right-1 h-6 w-6'} flex items-center justify-center rounded-full bg-white/90 dark:bg-black/80 text-slate-500 hover:text-red-500 shadow-sm transition-colors`}
                        title={labels.uploadCancel}
                        aria-label={labels.uploadCancel}
                    >
                        <Icon name="close" className={size === 'sm' ? '!text-[12px]' : '!text-[14px]'} />
                    </button>
                )}
            </div>
        );
    }

    // ── Marked-for-deletion: red ring + grayscale, single undo overlay.
    if (isMarkedDelete) {
        return (
            <div className={`relative group ${radiusCls} overflow-hidden aspect-square border border-red-500 opacity-50 bg-red-50 ${className}`}>
                <img
                    src={url}
                    alt={alt}
                    draggable={false}
                    className="w-full h-full object-cover grayscale pointer-events-none"
                />
                {onUndoDelete && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onUndoDelete(); }}
                            className="bg-white/80 dark:bg-black/80 p-1.5 rounded-full text-red-500 hover:text-red-700 shadow-sm transition-colors"
                            title={undoAriaLabel}
                            aria-label={undoAriaLabel}
                        >
                            <Icon name="undo" className={`${iconCls}`} />
                        </button>
                    </div>
                )}
            </div>
        );
    }

    const borderCls = isDragging
        ? 'opacity-50 scale-95 border-primary ring-2 ring-primary/30'
        : isOverLimit
            ? 'border-red-500 ring-2 ring-red-500 scale-95 grayscale-[50%]'
            : isPending
                ? 'border-emerald-500/60 dark:border-emerald-500/60 bg-emerald-500/5 dark:bg-emerald-500/10'
                : 'border-border-subtle dark:border-border-subtle-dark bg-slate-50 dark:bg-surface-hover-dark';

    const hiddenCls = isStateHidden ? 'opacity-50 grayscale' : '';

    const cursorCls = draggable ? 'cursor-move' : onClick ? 'cursor-pointer' : '';

    return (
        <div
            draggable={draggable}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
            className={`relative group ${radiusCls} overflow-hidden aspect-square border ${cursorCls} transition-all ${borderCls} ${hiddenCls} ${className}`}
            title={badgeTooltip}
        >
            <img
                src={url}
                alt={alt}
                draggable={false}
                className="w-full h-full object-cover pointer-events-none"
            />

            {isCover && (
                <div
                    className={`absolute ${starCls} z-10 bg-yellow-400 text-white rounded-full shadow-sm border border-yellow-500 flex items-center justify-center`}
                    title={coverLabel}
                >
                    <Icon name="star" className={`${starIconCls}`} />
                </div>
            )}

            {badge && (
                <div
                    className={`absolute ${badgeCls} z-10 bg-black/70 text-white rounded font-bold leading-none shadow-sm pointer-events-none`}
                    aria-label={badgeTooltip}
                >
                    {badge}
                </div>
            )}

            {isOverLimit && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-900/40 backdrop-blur-[1px] pointer-events-none">
                    <Icon name="block" className="!text-[48px] text-red-500 drop-shadow-md" />
                    {overLimitLabel && (
                        <span className="mt-1 text-[10px] font-bold text-white drop-shadow-md">{overLimitLabel}</span>
                    )}
                </div>
            )}

            {(onDownload || onToggleHidden || onDelete) && (
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    {onDownload && (
                        <a
                            href={onDownload.url}
                            download={onDownload.fileName ?? downloadFileName}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            draggable={false}
                            className="text-white hover:text-green-400 transition-colors"
                            title={downloadAriaLabel}
                            aria-label={downloadAriaLabel}
                        >
                            <Icon name="download" className={`${iconCls}`} />
                        </a>
                    )}
                    {onToggleHidden && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onToggleHidden(); }}
                            className={`text-white transition-colors ${isStateHidden ? 'hover:text-emerald-300' : 'hover:text-amber-300'}`}
                            title={isStateHidden ? showAriaLabel : hideAriaLabel}
                            aria-label={isStateHidden ? showAriaLabel : hideAriaLabel}
                        >
                            <Icon name={isStateHidden ? 'visibility' : 'visibility_off'} className={`${iconCls}`} />
                        </button>
                    )}
                    {onDelete && !disableDelete && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onDelete(); }}
                            className="text-white hover:text-red-400 transition-colors"
                            title={deleteAriaLabel}
                            aria-label={deleteAriaLabel}
                        >
                            <Icon name="delete" className={`${iconCls}`} />
                        </button>
                    )}
                    {disableDelete && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); }}
                            className="text-slate-400 cursor-default transition-colors"
                            title={disableDeleteReason}
                            aria-label={disableDeleteReason}
                            disabled
                        >
                            <Icon name="delete" className={`${iconCls}`} />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default PhotoThumb;
